import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { botService } from '../services/bot'
import { BotState, Order } from '@/types'
import { formatPrice, formatNumber } from '@/utils/formatters'

// Interfaces para los datos de monitoreo adicionales
interface TradeHistory {
  id: string
  tipo: string
  lado: string
  cantidad: number
  precio: number
  timestamp: string
  ganancia?: number
}

const MonitoringPage: React.FC = () => {
  // Estados para los datos
  const [botStatus, setBotStatus] = useState<BotState | null>(null)
  const [gridOrders, setGridOrders] = useState<Order[]>([])
  const [tradeHistory, setTradeHistory] = useState<TradeHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [isStarting, setIsStarting] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Cargar datos
  useEffect(() => {
    loadMonitoringData()
  }, [])

  // Auto-refresh cada 5 segundos
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    if (autoRefresh) {
      interval = setInterval(() => {
        loadMonitoringData()
      }, 5000)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [autoRefresh])

  const loadMonitoringData = async () => {
    try {
      const [status, orders, history] = await Promise.all([
        botService.getBotStatus(),
        botService.getGridOrders(),
        botService.getTradeHistory()
      ])
      setBotStatus(status)
      setGridOrders(orders)
      setTradeHistory(history)
    } catch (error) {
      console.error('Error loading monitoring data:', error)
    } finally {
      setLoading(false)
    }
  }

  const startBot = async () => {
    setIsStarting(true)
    try {
      await botService.startBot()
      await loadMonitoringData()
    } catch (error) {
      console.error('Error starting bot:', error)
      toast.error('Error al iniciar el bot')
    } finally {
      setIsStarting(false)
    }
  }

  const stopBot = async () => {
    setIsStopping(true)
    try {
      await botService.stopBot()
      await loadMonitoringData()
    } catch (error) {
      console.error('Error stopping bot:', error)
      toast.error('Error al detener el bot')
    } finally {
      setIsStopping(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header con título y controles principales */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">📊 Panel de Monitoreo - Grid Trading</h1>
            <div className="flex items-center space-x-4">
              {/* Toggle auto-refresh */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autoRefresh"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="autoRefresh" className="ml-2 text-sm text-gray-700">
                  Auto-actualizar (5s)
                </label>
              </div>
              {/* Botón refresh manual */}
              <button
                onClick={loadMonitoringData}
                className="bg-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 text-sm"
              >
                🔄 Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Estado del Bot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estado General */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">🤖 Estado del Bot</h2>
          </div>
          <div className="p-6">
            {botStatus && (
              <div className="space-y-4">
                {/* Estado principal */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Estado:</span>
                  <div className="flex items-center">
                    <span className={`h-3 w-3 rounded-full mr-2 ${
                      botStatus.activo ? 'bg-green-500' : 'bg-red-500'
                    }`}></span>
                    <span className={`font-medium ${
                      botStatus.activo ? 'text-green-700' : 'text-red-700'
                    }`}>
                      {botStatus.activo ? 'ACTIVO' : 'INACTIVO'}
                    </span>
                  </div>
                </div>

                {/* Modo */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Modo:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    botStatus.modo_dry_run 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {botStatus.modo_dry_run ? 'SIMULACIÓN' : 'REAL'}
                  </span>
                </div>

                {/* Exchange y Par */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Exchange:</span>
                  <span className="text-sm text-gray-900">{botStatus.exchange}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Par:</span>
                  <span className="text-sm font-bold text-blue-600">{botStatus.par}</span>
                </div>

                {/* Precio actual */}
                {botStatus.precio_actual && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Precio Actual:</span>
                    <span className="text-sm font-bold text-green-600">
                      {formatPrice(botStatus.precio_actual, 4)}
                    </span>
                  </div>
                )}

                {/* Uptime */}
                {botStatus.uptime && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Tiempo activo:</span>
                    <span className="text-sm text-gray-900">{botStatus.uptime}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Controles del Bot */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">🎮 Controles</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {botStatus && !botStatus.activo ? (
                <button
                  onClick={startBot}
                  disabled={isStarting}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 font-medium"
                >
                  {isStarting ? '⏳ Iniciando...' : '▶️ Iniciar Bot'}
                </button>
              ) : (
                <button
                  onClick={stopBot}
                  disabled={isStopping}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 font-medium"
                >
                  {isStopping ? '⏳ Deteniendo...' : '⏸️ Detener Bot'}
                </button>
              )}
              
              <div className="border-t pt-4">
                <p className="text-xs text-gray-600 text-center">
                  💡 Tip: Usa el modo simulación para probar antes de operar con dinero real
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Métricas del Trading */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Capital disponible */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">💰</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Capital</p>
              <p className="text-lg font-semibold text-gray-900">
                {formatPrice(botStatus?.capital_disponible || 0, 2)}
              </p>
            </div>
          </div>
        </div>

        {/* Posiciones abiertas */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">📈</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Posiciones</p>
              <p className="text-lg font-semibold text-gray-900">
                {botStatus?.posiciones_abiertas || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Órdenes pendientes */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-bold">⏱️</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Órdenes</p>
              <p className="text-lg font-semibold text-gray-900">
                {botStatus?.ordenes_pendientes || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Ganancia total */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">💎</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">P&L Total</p>
              <p className={`text-lg font-semibold ${
                (botStatus?.ganancia_total || 0) >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatPrice(botStatus?.ganancia_total || 0, 2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Órdenes del Grid */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">🔧 Órdenes del Grid</h2>
        </div>
        <div className="p-6">
          {gridOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tiempo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {gridOrders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.side === 'buy' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.side === 'buy' ? '📈 BUY' : '📉 SELL'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {order.amount}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                        {order.price ? formatPrice(order.price, 4) : 'Market'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          order.status === 'closed' 
                            ? 'bg-green-100 text-green-800'
                            : order.status === 'open'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(order.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay órdenes activas</h3>
              <p className="text-gray-500">Las órdenes del grid aparecerán aquí cuando el bot esté activo</p>
            </div>
          )}
        </div>
      </div>

      {/* Historial de Trades */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">📊 Historial de Operaciones</h2>
        </div>
        <div className="p-6">
          {tradeHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Precio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      P&L
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tiempo
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tradeHistory.slice(0, 10).map((trade) => (
                    <tr key={trade.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trade.tipo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          trade.lado === 'buy' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {trade.lado === 'buy' ? '📈 BUY' : '📉 SELL'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {trade.cantidad}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-600">
                        {formatPrice(trade.precio, 4)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {trade.ganancia !== undefined && (
                          <span className={`text-sm font-medium ${
                            trade.ganancia >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {trade.ganancia >= 0 ? '+' : ''}{formatPrice(trade.ganancia, 2)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(trade.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay historial de trades</h3>
              <p className="text-gray-500">Los trades ejecutados aparecerán aquí</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MonitoringPage